import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import { useField } from "../form/Form";
import Icon from "../icons/Icon";
import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";

import "./Radio.scss";
import { createDataAttributes, recursiveMap } from "../../utilities";

/**
 *
 * THE CONTAINER
 *
 */

export const RadioButtonContainer = ({
  id,
  name,
  value,
  className,
  label,
  disabled,
  onChange,
  onValidate,
  required,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  dataAttrib,
  children
}) => {
  useField({ name, validation: { required }, onValidate, label });

  const containerClasses = classnames(
    "uikit__radio-button-container__wrapper",
    "uikit__form-item",
    className,
    {
      "uikit__radio-button__disabled": disabled,
      "has-error": !!fieldErrors?.length,
      "is-required": required,
      "uikit__hide-label": hideLabel,
      "uikit__form__hide-errors": hideErrors
    }
  );

  const handleChange = event => {
    onChange({
      event,
      field: name,
      value: event.currentTarget.value
    });
  };

  return (
    <div
      role="radiogroup"
      aria-labelledby={!noLabel && `radiogroup-label-${id}`}
      className={containerClasses}
      {...createDataAttributes(dataAttrib)}
      aria-describedby={`errors_for_${id}`}
    >
      {!noLabel && label && (
        <span
          className="uikit__radio-button-container__header"
          id={`radiogroup-label-${id}`}
        >
          <span className="uikit__label__text">{label}</span>
          {required && (
            <span>
              <Icon className="uikit__required-asterisk" icon="farAsterisk" />
              <span className="uikit-sr-only">required</span>
            </span>
          )}
        </span>
      )}
      <div className="uikit__radio-button-container">
        {recursiveMap(children, child => {
          const addedProps =
            child?.props?.componentname === "RadioButton"
              ? { handleChange, name, value, disabled }
              : {};

          return React.cloneElement(child, {
            ...child.props,
            ...addedProps
          });
        })}
      </div>
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

RadioButtonContainer.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.string.isRequired,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  children: propTypes.any,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object)
};

RadioButtonContainer.defaultProps = {
  hideErrors: false,
  componentname: "RadioButtonContainer"
};

export default RadioButtonContainer;

/**
 *
 * THE BUTTON
 *
 */
export const RadioButton = ({
  id,
  label,
  optionValue,
  name,
  value,
  handleChange,
  disabled,
  compact,
  className,
  dataAttrib,
  ...rest
}) => {
  const classNames = classnames("uikit__radio-button__option", className, {
    "uikit__radio-button__compact": compact,
    "uikit__radio-button__selected": value === optionValue
  });

  return (
    <span
      className={classNames}
      {...rest}
      {...createDataAttributes(dataAttrib)}
    >
      <Label
        htmlFor={`${name}_${id}`}
        className="uikit__radio"
        label={label}
        labelPosition="after"
      >
        <input
          type="radio"
          id={`${name}_${id}`}
          name={name}
          value={optionValue}
          className={classnames({
            "is-selected": value === optionValue
          })}
          checked={value === optionValue}
          onChange={handleChange}
          disabled={disabled}
        />
        <span className="uikit__radio__icon"> </span>
      </Label>
    </span>
  );
};

RadioButton.propTypes = {
  disabled: propTypes.bool,
  className: propTypes.string,
  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  name: propTypes.string,
  handleChange: propTypes.func,
  value: propTypes.string,
  children: propTypes.any,

  //component-specific
  componentname: propTypes.string,
  optionValue: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.bool
  ]).isRequired,
  compact: propTypes.bool,
  dataAttrib: propTypes.arrayOf(Object)
};

RadioButton.defaultProps = {
  componentname: "RadioButton"
};
